import { useEffect, useRef, useCallback, useState } from "react";

/**
 * Public-style WebSocket hook for unauthenticated clients (share-link portal,
 * referrer dashboard). Pass the full WS path including any token segment.
 *
 * Example:
 *   usePublicWebSocket(`/api/ws/portal/${shareToken}`, handleMessage)
 *   usePublicWebSocket(`/api/ws/referrer/${refToken}/${caseId}`, handleMessage)
 */
export function usePublicWebSocket(path, onMessage) {
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const reconnectRef = useRef(null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const connect = useCallback(() => {
    if (!path) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const wsUrl = backendUrl.replace(/^http/, "ws") + path;
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => {
        setConnected(true);
        const ping = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send("ping");
        }, 30000);
        ws._pingInterval = ping;
      };
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type !== "pong" && onMessageRef.current) {
            onMessageRef.current(data);
          }
        } catch {}
      };
      ws.onclose = () => {
        setConnected(false);
        if (ws._pingInterval) clearInterval(ws._pingInterval);
        reconnectRef.current = setTimeout(connect, 3000);
      };
      ws.onerror = () => { ws.close(); };
    } catch {}
  }, [path]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        if (wsRef.current._pingInterval) clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
      }
    };
  }, [connect]);

  return { connected };
}
